import { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Divider,
  Typography
} from '@mui/material';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { Formik } from 'formik';
import useScriptRef from 'hooks/useScriptRef.js';
import AnimateButton from 'ui-component/extended/AnimateButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import Cookies from 'js-cookie';
import { setStoredPermissions } from 'utils/permissionsStorage';

const FirebaseLogin = ({ ...others }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const scriptedRef = useScriptRef();
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleCredentialClick = async (setFieldValue, handleSubmit, values) => {
    // Call the same login logic as the form submit
    try {
      setIsSubmitting(true);

      const identifier = (values.email || '').trim();
      const payload = { password: values.password };
      // if (identifier.includes('@')) payload.email = identifier;
      payload.loginId = identifier;

      const response = await axios.post(`${REACT_APP_BACKEND_URL}/student/login`, payload);

      console.log('Login Response:', response?.data);

      if (response?.data?.token) {
        // Decode token to get actual expiration time
        const decodedToken = jwtDecode(response?.data?.token);
        const tokenExpiry = new Date(decodedToken.exp * 1000); // Convert from seconds to milliseconds

        // Set cookies with expiration time
        Cookies.set('Token', response?.data?.token, { expires: tokenExpiry });
        Cookies.set('_Id', response?.data?.student?._id || '', { expires: tokenExpiry });
        Cookies.set('Role', response?.data?.student?.role || '', { expires: tokenExpiry });
        Cookies.set('user', JSON.stringify(response?.data?.student || {}), { expires: tokenExpiry });

        toast.success('Login successful!');

        if (response?.data?.student?.role?.toLowerCase() === 'student') {
          navigate('/dashboard/default');
          window.location.reload();
        } else {
          navigate('/superadmindashboard/default');
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('Login Error:', error);
      console.error('Error Response:', error?.response?.data);
      toast.error(error?.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={{
          email: '',
          password: ''
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string().max(255).required('Email or Uid is required'),
          password: Yup.string().required('Password is required')
        })}
        onSubmit={async (values, { setErrors, setStatus }) => {
          try {
            setIsSubmitting(true);

            const identifier = (values.email || '').trim();
            const payload = { password: values.password };
            // if (identifier.includes('@')) payload.email = identifier;
            payload.loginId = identifier;

            const response = await axios.post(`${REACT_APP_BACKEND_URL}/student/login`, payload);

            console.log('Login Response:->', response?.data);

            // if (response?.data?.token) {
            //   Cookies.set('Token', response?.data?.token);
            //   Cookies.set('_Id', response?.data?.user._id);
            //   Cookies.set('Role', response?.data?.user.role);
            //   Cookies.set('user', JSON.stringify(response?.data?.user));

            //   if (response?.data?.user?.role === 'Customer') {
            //     navigate('/dashboard/default');
            //     window.location.reload();
            //   } else {
            //     navigate('/superadmindashboard/default');
            //     window.location.reload();
            //   }
            // }

            if (response?.data?.token) {
              // Decode token to get actual expiration time
              const decodedToken = jwtDecode(response?.data?.token);
              const tokenExpiry = new Date(decodedToken.exp * 1000); // Convert from seconds to milliseconds

              // Set cookies with expiration time
              Cookies.set('Token', response?.data?.token, { expires: tokenExpiry });
              Cookies.set('_Id', response?.data?.student?._id || '', { expires: tokenExpiry });
              Cookies.set('Role', response?.data?.student?.role || '', { expires: tokenExpiry });
              Cookies.set('user', JSON.stringify(response?.data?.student || {}), { expires: tokenExpiry });
              Cookies.set('isPasswordChanged', response?.data?.student?.is_password_changed ? 'true' : 'false', { expires: tokenExpiry });

              setStoredPermissions(response?.data?.student?.permissions || [], { expires: tokenExpiry });

              toast.success('Login successful!');
              sessionStorage.setItem('FROM_LOGIN', 'true');

              if (response?.data?.student?.role?.toLowerCase() === 'student') {
                navigate('/dashboard/default');
              } else if (response?.data?.student?.role?.toLowerCase() === 'admin') {
                navigate('/superadmindashboard/default');
              } else {
                navigate('/supermostadmin/default');
              }
            }
          } catch (error) {
            console.error('Login Error:', error);
            console.error('Error Response:', error?.response?.data);
            toast.error(error?.response?.data?.message || 'Login failed. Please try again.');
          } finally {
            setIsSubmitting(false);
          }
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, touched, values, setFieldValue }) => (
          <form noValidate onSubmit={handleSubmit} {...others}>
            <FormControl
              fullWidth
              error={Boolean(touched.email && errors.email)}
              sx={{
                '& .MuiFormLabel-root': {
                  color: '#000066'
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: errors.email ? '#000066' : ''
                  }
                },
                mb: 2
              }}
            >
              <FormLabel htmlFor="outlined-adornment-email-login" sx={{ mb: 0.5 }}>
                Login ID
              </FormLabel>
              <OutlinedInput
                id="outlined-adornment-email-login"
                type="text"
                value={values.email}
                name="email"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="Enter Login ID"
              />
              {touched.email && errors.email && <FormHelperText error>{errors.email}</FormHelperText>}
            </FormControl>
            <FormControl
              fullWidth
              error={Boolean(touched.password && errors.password)}
              sx={{
                '& .MuiFormLabel-root': {
                  color: '#000066'
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: errors.email ? '#000066' : ''
                  }
                }
              }}
            >
              <FormLabel htmlFor="outlined-adornment-password-login" sx={{ mb: 0.5 }}>
                Password
              </FormLabel>
              <OutlinedInput
                id="outlined-adornment-password-login"
                type={showPassword ? 'text' : 'password'}
                value={values.password}
                name="password"
                onBlur={handleBlur}
                onChange={handleChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
                placeholder="Enter password"
              />
              {touched.password && errors.password && <FormHelperText error>{errors.password}</FormHelperText>}
            </FormControl>

            {/* <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  cursor: 'pointer',
                  p: 3,
                  color: 'white',
                  textAlign: 'center',
                  borderRadius: 1
                }}
                onClick={() => handleCredentialClick(setFieldValue, handleSubmit, values)}
              >
                <Typography variant="h5">Login with Hostel Credentials</Typography>
              </Box>
            </Box> */}

            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <AnimateButton>
                <Button disableElevation disabled={isSubmitting} size="large" type="submit" variant="contained" color="secondary">
                  {isSubmitting ? 'Logging in...' : 'Sign in'}
                </Button>
              </AnimateButton>
            </Box>
          </form>
        )}
      </Formik>
    </>
  );
};

export default FirebaseLogin;
